import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { LogOut, LogIn, Search, Settings as SettingsIcon, Menu, X, Users, Trophy, Shield, Crown, QrCode } from "lucide-react";
import { SPORTS, SPORT_IDS } from "@/lib/sports";
import { useSettings } from "@/contexts/SettingsContext";
import SearchPalette from "@/components/SearchPalette";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";

export default function SiteHeader({ playerName = null, accentColor = "#D4AF37", showSportTabs = false }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const [params, setParams] = useSearchParams();
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const isPlayers = pathname === "/" || pathname.startsWith("/player");
  const isTournaments = pathname.startsWith("/tournament");
  const isChampionships = pathname.startsWith("/championship");
  const isTeams = pathname.startsWith("/team") || pathname.startsWith("/admin/teams");
  const activeSport = params.get("sport");
  const sportTabsVisible = showSportTabs && settings?.show_sport_tabs !== false;

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const setSport = (s) => {
    const next = new URLSearchParams(params);
    if (s) next.set("sport", s);
    else next.delete("sport");
    setParams(next, { replace: true });
  };

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 backdrop-blur-xl bg-[#050505]/70 border-b border-white/10"
    >
      <div className="max-w-7xl mx-auto w-full px-4 md:px-8 h-16 flex items-center justify-between gap-4">
        {user?.role === "admin" ? (
          <Link
            to="/"
            data-testid="header-home-link"
            className="flex items-center gap-2.5 group shrink-0"
          >
            <img
              src="/laser-tech-logo.png"
              alt="Laser Tech Création"
              className="h-10 w-10 object-contain"
            />
            <span className="hidden sm:block font-display text-lg font-black tracking-tight uppercase leading-none">
              Laser Tech
              <span className="block text-[10px] font-normal tracking-[0.25em] text-zinc-400">
                Création · Genève
              </span>
            </span>
          </Link>
        ) : (
          <div
            data-testid="header-brand"
            className="flex items-center gap-2.5 shrink-0"
          >
            <img
              src="/laser-tech-logo.png"
              alt="Laser Tech Création"
              className="h-10 w-10 object-contain"
            />
            <span className="hidden sm:block font-display text-lg font-black tracking-tight uppercase leading-none">
              Laser Tech
              <span className="block text-[10px] font-normal tracking-[0.25em] text-zinc-400">
                Création · Genève
              </span>
            </span>
          </div>
        )}

        {playerName ? (
          <div className="flex items-center gap-2">
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="hidden sm:block font-display uppercase tracking-wider text-sm truncate"
              data-testid="header-player-name"
              style={{ color: accentColor }}
            >
              {playerName}
            </motion.div>
            {user && (
              <button
                type="button"
                data-testid="header-search-btn-compact"
                onClick={() => setSearchOpen(true)}
                aria-label="Rechercher"
                className="w-9 h-9 rounded-full inline-flex items-center justify-center text-zinc-400 hover:text-white border border-white/10 transition-colors"
              >
                <Search className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        ) : !user || user.role !== "admin" ? (
          <div className="flex items-center gap-3">
            {user?.role === "organizer" ? (
              <Link
                to="/organizer"
                data-testid="header-organizer-tag"
                className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-body hover:text-white transition-colors"
              >
                Espace Organisateur
              </Link>
            ) : (
              <div data-testid="header-visitor-tag" className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-body">
                {user?.role === "coach" ? (
                  <Link to="/coach" className="hover:text-white transition-colors">
                    Espace Coach
                  </Link>
                ) : (
                  "Médaille connectée"
                )}
              </div>
            )}
            {user && (
              <button
                type="button"
                data-testid="header-logout-btn"
                onClick={() => {
                  logout();
                  navigate("/login");
                }}
                className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-400 hover:text-white border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] rounded-full px-3 py-1.5 transition-colors"
              >
                <LogOut className="w-3 h-3" /> Déconnexion
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Desktop nav (md+) */}
            <nav className="hidden md:flex items-center gap-1.5 text-xs">
              <button
                type="button"
                data-testid="header-search-btn"
                onClick={() => setSearchOpen(true)}
                aria-label="Rechercher"
                className="px-3 py-1.5 rounded-full uppercase tracking-[0.18em] font-medium transition-colors text-zinc-400 hover:text-white border border-white/10 inline-flex items-center gap-1.5"
              >
                <Search className="w-3 h-3" />
                <span className="hidden md:inline">Rechercher</span>
                <kbd className="hidden md:inline ml-1 px-1 py-0.5 text-[9px] rounded border border-white/10 bg-white/5 text-zinc-500 font-body normal-case tracking-normal">
                  ⌘K
                </kbd>
              </button>
              <Link
                to="/"
                data-testid="nav-players"
                className={`px-3 py-1.5 rounded-full uppercase tracking-[0.18em] font-medium transition-all ${
                  isPlayers ? "text-black" : "text-zinc-400 hover:text-white border border-white/10"
                }`}
                style={
                  isPlayers
                    ? { background: "linear-gradient(180deg, #FFFFFF 0%, #D9D9D9 100%)", borderColor: "#FFFFFF" }
                    : {}
                }
              >
                Joueurs
              </Link>
              <Link
                to="/tournaments"
                data-testid="nav-tournaments"
                className={`px-3 py-1.5 rounded-full uppercase tracking-[0.18em] font-medium transition-all ${
                  isTournaments ? "text-black" : "text-zinc-400 hover:text-white border border-white/10"
                }`}
                style={
                  isTournaments
                    ? { background: "linear-gradient(180deg, #FFE9A8 0%, #D4AF37 60%, #B8941F 100%)", borderColor: "#D4AF37" }
                    : {}
                }
              >
                Tournois
              </Link>
              <Link
                to="/championships"
                data-testid="nav-championships"
                className={`px-3 py-1.5 rounded-full uppercase tracking-[0.18em] font-medium transition-all ${
                  isChampionships ? "text-black" : "text-zinc-400 hover:text-white border border-white/10"
                }`}
                style={
                  isChampionships
                    ? { background: "linear-gradient(180deg, #FFE9A8 0%, #D4AF37 60%, #B8941F 100%)", borderColor: "#D4AF37" }
                    : {}
                }
              >
                Championnats
              </Link>
              {user?.role === "admin" && (
                <Link
                  to="/admin/teams"
                  data-testid="nav-teams"
                  className={`px-3 py-1.5 rounded-full uppercase tracking-[0.18em] font-medium transition-all ${
                    isTeams ? "text-black" : "text-zinc-400 hover:text-white border border-white/10"
                  }`}
                  style={
                    isTeams
                      ? { background: "linear-gradient(180deg, #C0C0C0 0%, #8A8A8A 100%)", borderColor: "#C0C0C0" }
                      : {}
                  }
                >
                  Équipes
                </Link>
              )}
              <div className="ml-2 h-5 w-px bg-white/10" />
              {user ? (
                <>
                  <Link
                    to="/admin/scans"
                    data-testid="nav-scans"
                    className="px-3 py-1.5 rounded-full uppercase tracking-[0.18em] font-medium transition-colors text-zinc-400 hover:text-white border border-white/10 inline-flex items-center gap-1.5"
                  >
                    <span>Scans</span>
                  </Link>
                  <Link
                    to="/admin/settings"
                    data-testid="nav-settings"
                    className="px-3 py-1.5 rounded-full uppercase tracking-[0.18em] font-medium transition-colors text-zinc-400 hover:text-white border border-white/10 inline-flex items-center gap-1.5"
                  >
                    <span>Paramètres</span>
                  </Link>
                  <button
                    type="button"
                    data-testid="nav-logout"
                    onClick={() => {
                      logout();
                      navigate("/");
                    }}
                    className="px-3 py-1.5 rounded-full uppercase tracking-[0.18em] font-medium transition-colors text-zinc-400 hover:text-white border border-white/10 inline-flex items-center gap-1.5"
                  >
                    <LogOut className="w-3 h-3" />
                    <span>Déconnexion</span>
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  data-testid="nav-login"
                  className="px-3 py-1.5 rounded-full uppercase tracking-[0.18em] font-medium transition-colors text-zinc-400 hover:text-white border border-white/10 inline-flex items-center gap-1.5"
                >
                  <LogIn className="w-3 h-3" />
                  <span>Admin</span>
                </Link>
              )}
            </nav>

            {/* Mobile nav (< md) — search icon + hamburger */}
            <div className="flex md:hidden items-center gap-2">
              <button
                type="button"
                data-testid="header-search-btn-mobile"
                onClick={() => setSearchOpen(true)}
                aria-label="Rechercher"
                className="w-9 h-9 rounded-full inline-flex items-center justify-center text-zinc-300 hover:text-white border border-white/10 transition-colors"
              >
                <Search className="w-4 h-4" />
              </button>
              <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
                <SheetTrigger asChild>
                  <button
                    type="button"
                    data-testid="header-mobile-menu-btn"
                    aria-label="Menu"
                    className="w-9 h-9 rounded-full inline-flex items-center justify-center text-zinc-300 hover:text-white border border-white/10 transition-colors"
                  >
                    <Menu className="w-4 h-4" />
                  </button>
                </SheetTrigger>
                <SheetContent
                  side="right"
                  data-testid="mobile-nav-sheet"
                  className="w-[85vw] sm:w-[360px] bg-[#050505] border-l border-white/10 text-white p-0"
                >
                  <SheetHeader className="px-5 pt-6 pb-4 border-b border-white/10 text-left">
                    <SheetTitle className="font-display text-base font-black tracking-tight uppercase text-white">
                      Navigation
                    </SheetTitle>
                    <SheetDescription className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-body">
                      Laser Tech Création
                    </SheetDescription>
                  </SheetHeader>

                  <nav className="flex flex-col p-3 gap-1 pb-mobile-safe md:pb-3">
                    <Link
                      to="/"
                      onClick={() => setMobileNavOpen(false)}
                      data-testid="mobile-nav-players"
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm uppercase tracking-[0.18em] font-medium transition-colors ${
                        isPlayers
                          ? "bg-white text-black"
                          : "text-zinc-300 hover:text-white hover:bg-white/5 border border-white/5"
                      }`}
                    >
                      <Users className="w-4 h-4" />
                      Joueurs
                    </Link>
                    <Link
                      to="/tournaments"
                      onClick={() => setMobileNavOpen(false)}
                      data-testid="mobile-nav-tournaments"
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm uppercase tracking-[0.18em] font-medium transition-colors ${
                        isTournaments
                          ? "text-black"
                          : "text-zinc-300 hover:text-white hover:bg-white/5 border border-white/5"
                      }`}
                      style={
                        isTournaments
                          ? { background: "linear-gradient(180deg, #FFE9A8 0%, #D4AF37 60%, #B8941F 100%)" }
                          : {}
                      }
                    >
                      <Trophy className="w-4 h-4" />
                      Tournois
                    </Link>
                    <Link
                      to="/championships"
                      onClick={() => setMobileNavOpen(false)}
                      data-testid="mobile-nav-championships"
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm uppercase tracking-[0.18em] font-medium transition-colors ${
                        isChampionships
                          ? "text-black"
                          : "text-zinc-300 hover:text-white hover:bg-white/5 border border-white/5"
                      }`}
                      style={
                        isChampionships
                          ? { background: "linear-gradient(180deg, #FFE9A8 0%, #D4AF37 60%, #B8941F 100%)" }
                          : {}
                      }
                    >
                      <Crown className="w-4 h-4" />
                      Championnats
                    </Link>
                    {user?.role === "admin" && (
                      <Link
                        to="/admin/teams"
                        onClick={() => setMobileNavOpen(false)}
                        data-testid="mobile-nav-teams"
                        className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm uppercase tracking-[0.18em] font-medium transition-colors ${
                          isTeams
                            ? "text-black"
                            : "text-zinc-300 hover:text-white hover:bg-white/5 border border-white/5"
                        }`}
                        style={
                          isTeams
                            ? { background: "linear-gradient(180deg, #C0C0C0 0%, #8A8A8A 100%)" }
                            : {}
                        }
                      >
                        <Shield className="w-4 h-4" />
                        Équipes
                      </Link>
                    )}

                    <div className="my-2 h-px bg-white/10" />

                    {user ? (
                      <>
                        <Link
                          to="/admin/scans"
                          onClick={() => setMobileNavOpen(false)}
                          data-testid="mobile-nav-scans"
                          className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm uppercase tracking-[0.18em] font-medium text-zinc-300 hover:text-white hover:bg-white/5 border border-white/5 transition-colors"
                        >
                          <QrCode className="w-4 h-4" />
                          Scans & vues
                        </Link>
                        <Link
                          to="/admin/settings"
                          onClick={() => setMobileNavOpen(false)}
                          data-testid="mobile-nav-settings"
                          className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm uppercase tracking-[0.18em] font-medium text-zinc-300 hover:text-white hover:bg-white/5 border border-white/5 transition-colors"
                        >
                          <SettingsIcon className="w-4 h-4" />
                          Paramètres
                        </Link>
                        <button
                          type="button"
                          data-testid="mobile-nav-logout"
                          onClick={() => {
                            setMobileNavOpen(false);
                            logout();
                            navigate("/");
                          }}
                          className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm uppercase tracking-[0.18em] font-medium text-zinc-300 hover:text-white hover:bg-white/5 border border-white/5 transition-colors text-left"
                        >
                          <LogOut className="w-4 h-4" />
                          Déconnexion
                        </button>
                      </>
                    ) : (
                      <Link
                        to="/login"
                        onClick={() => setMobileNavOpen(false)}
                        data-testid="mobile-nav-login"
                        className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm uppercase tracking-[0.18em] font-medium text-zinc-300 hover:text-white hover:bg-white/5 border border-white/5 transition-colors"
                      >
                        <LogIn className="w-4 h-4" />
                        Admin
                      </Link>
                    )}
                  </nav>
                </SheetContent>
              </Sheet>
            </div>
          </>
        )}
      </div>

      {/* Sport tabs (admin only) */}
      {user?.role === "admin" && sportTabsVisible && !playerName && (
        <div className="border-t border-white/5 bg-black/40 backdrop-blur-xl">
          <div
            className="max-w-7xl mx-auto w-full px-4 md:px-8 py-3 flex items-center gap-2 overflow-x-auto"
            data-testid="sport-tabs"
          >
            <button
              type="button"
              onClick={() => setSport(null)}
              data-testid="sport-tab-all"
              data-active={!activeSport}
              className="metal-pill shrink-0 px-3.5 py-1.5 rounded-full text-[10px] uppercase tracking-[0.28em] font-bold"
              style={{ "--metal-accent": "#FFFFFF" }}
            >
              Tous
            </button>
            {SPORT_IDS.filter((s) => (settings?.enabled_sports || SPORT_IDS).includes(s)).map((s) => {
              const sport = SPORTS[s];
              const active = activeSport === s;
              return (
                <button
                  type="button"
                  key={s}
                  onClick={() => setSport(s)}
                  data-testid={`sport-tab-${s}`}
                  data-active={active}
                  className="metal-pill shrink-0 px-3.5 py-1.5 rounded-full text-[10px] uppercase tracking-[0.28em] font-bold"
                  style={{ "--metal-accent": sport.color }}
                >
                  {sport.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
      <SearchPalette open={searchOpen} onOpenChange={setSearchOpen} />
    </header>
  );
}
